
import { useState } from "react";
import { LensInventory, LensStatus } from "@/types/inventory";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, ArrowRight } from "lucide-react";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LensFormValues, lensSchema } from "@/types/inventory";
import { AddLensForm } from "./AddLensForm";
import { Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

export function LensCard({ lens, onActionComplete }: { lens: LensInventory, onActionComplete: () => void }) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  // Type assertions to ensure lens properties match expected types
  const lensStatus = lens.status as LensStatus;
  const lensPurchasePrice = lens.purchase_price ? lens.purchase_price.toString() : '';
  
  const form = useForm<LensFormValues>({
    resolver: zodResolver(lensSchema),
    defaultValues: {
      brand: lens.brand,
      lens_name: lens.lens_name,
      mount: lens.mount,
      status: lensStatus,
      serial_number: lens.serial_number || '',
      purchase_date: lens.purchase_date || '',
      purchase_price: lensPurchasePrice,
      notes: lens.notes || '',
    },
  });

  const handleEdit = async (values: LensFormValues) => {
    try {
      const { error } = await supabase
        .from('lens_inventory')
        .update({
          brand: values.brand,
          lens_name: values.lens_name,
          mount: values.mount,
          status: values.status,
          serial_number: values.serial_number || null,
          purchase_date: values.purchase_date || null,
          purchase_price: values.purchase_price ? parseFloat(values.purchase_price) : null,
          notes: values.notes || null,
        })
        .eq('id', lens.id);

      if (error) throw error;

      toast.success('Lens updated successfully');
      setIsEditing(false);
      onActionComplete();
    } catch (error: any) {
      toast.error(`Error updating lens: ${error.message}`);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('lens_inventory')
        .delete()
        .eq('id', lens.id);

      if (error) throw error;

      toast.success('Lens deleted successfully');
      setIsDeleting(false);
      setShowDeleteAlert(false);
      onActionComplete();
    } catch (error: any) {
      toast.error(`Error deleting lens: ${error.message}`);
      setIsDeleting(false);
      setShowDeleteAlert(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'working':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'needs_repair':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
      case 'for_parts':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-all duration-300">
      <CardContent className="p-0">
        <Link to={`/lens/${lens.id}`} className="block p-4 hover:bg-muted/30 transition-colors">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-medium">{lens.brand} {lens.lens_name}</h3>
              <p className="text-sm text-muted-foreground">
                {lens.mount}
              </p>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(lensStatus)}`}>
              {formatStatus(lensStatus)}
            </span>
          </div>
          
          <div className="flex items-center justify-end mt-3 text-primary text-sm">
            <span>{t('view_details')}</span>
            <ArrowRight className="h-3 w-3 ml-1" />
          </div>
        </Link>
        
        <div className="bg-muted/30 p-2 flex justify-end space-x-2 border-t">
          <Dialog open={isEditing} onOpenChange={setIsEditing}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm">
                <Edit className="h-4 w-4 mr-1" />
                {t('edit')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl sm:max-w-4xl md:max-w-4xl w-[95%]">
              <DialogHeader>
                <DialogTitle>{t('edit_lens')}</DialogTitle>
                <DialogDescription>
                  {t('update_lens_details')}
                </DialogDescription>
              </DialogHeader>
              <AddLensForm form={form} onSubmit={handleEdit} isEditing={true} />
            </DialogContent>
          </Dialog>
          
          <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowDeleteAlert(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t('delete')}
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the {lens.brand} {lens.lens_name} lens from your inventory. 
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction 
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : t('delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}
